import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  MapPin, Store, Clock, Tag, ArrowLeft, ShoppingCart, Flame
} from 'lucide-react';
import Logo from '../components/Logo';
import SiteDisclaimer from '../components/legal/SiteDisclaimer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISCOUNT_LABELS = {
  1: { label: '50% OFF', color: 'bg-emerald-500', icon: Tag },
  2: { label: '60% OFF', color: 'bg-orange-500', icon: Flame },
  3: { label: '75% OFF', color: 'bg-red-500', icon: Flame },
};

export default function RetailerStorefront() {
  const { slug } = useParams();
  const [storefront, setStorefront] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) fetchStorefront();
  }, [slug]);

  const fetchStorefront = async () => {
    try {
      const res = await fetch(`${API_URL}/api/directory/retailers/${slug}`);
      if (res.ok) {
        setStorefront(await res.json());
      } else if (res.status === 404) {
        setError('Retailer not found');
      } else {
        setError('Failed to load storefront');
      }
    } catch (e) {
      setError('Unable to connect');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400">Loading storefront...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="storefront-error">
        <div className="text-center">
          <Store className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 mb-4">{error}</p>
          <Link to="/grocers" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
            Back to Grocers Directory
          </Link>
        </div>
      </div>
    );
  }

  const { retailer, stores, rshds } = storefront;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="retailer-storefront">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/grocers" className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Logo size="small" />
          </div>
          <Link
            to="/consumer"
            data-testid="storefront-consumer-login"
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Sign In to Order
          </Link>
        </div>
      </header>

      {/* Store Banner */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0">
              {retailer.logo_url ? (
                <img src={retailer.logo_url} alt={retailer.name} className="w-14 h-14 rounded-lg object-cover" />
              ) : (
                <Store className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{retailer.name}</h1>
              {retailer.description && (
                <p className="text-purple-100 text-sm mt-1">{retailer.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2">
                <Badge className="bg-white/20 text-white border-0 text-xs">
                  {rshds.length} Active Deals
                </Badge>
                {stores[0]?.address && (
                  <span className="text-purple-200 text-xs flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {stores[0].address}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deals Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Available Deals
        </h2>

        {rshds.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Tag className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-2">No active deals right now</p>
              <p className="text-xs text-gray-400">Check back soon — new sizzling hot deals are posted daily!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {rshds.map((rshd) => {
              const discount = DISCOUNT_LABELS[rshd.discount_level] || DISCOUNT_LABELS[1];
              const dealPrice = rshd.original_price
                ? (rshd.original_price * (rshd.discount_level === 3 ? 0.25 : rshd.discount_level === 2 ? 0.40 : 0.50)).toFixed(2)
                : rshd.price;

              return (
                <Card key={rshd.id || rshd.barcode} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`rshd-card-${rshd.id || rshd.barcode}`}>
                  <div className="relative">
                    {rshd.image_url ? (
                      <img src={rshd.image_url} alt={rshd.name} className="w-full h-40 object-cover" />
                    ) : (
                      <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                        <ShoppingCart className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                    <Badge className={`absolute top-2 right-2 ${discount.color} text-white border-0 text-xs font-bold`}>
                      {discount.label}
                    </Badge>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{rshd.name}</h3>
                    {rshd.brand && (
                      <p className="text-xs text-gray-500">{rshd.brand}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <span className="text-lg font-bold text-purple-600">${dealPrice}</span>
                        {rshd.original_price && (
                          <span className="text-xs text-gray-400 line-through ml-1">
                            ${rshd.original_price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      {rshd.quantity_available && (
                        <span className="text-xs text-gray-400">
                          {rshd.quantity_available} left
                        </span>
                      )}
                    </div>
                    {rshd.expires_at && (
                      <p className="text-xs text-orange-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        Expires: {new Date(rshd.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 text-center">
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-purple-800 mb-2">
                Want to order these deals?
              </h3>
              <p className="text-sm text-purple-600 mb-4">
                Sign in or create a free DealShaq consumer account to start saving.
              </p>
              <Link to="/consumer">
                <Button className="bg-purple-600 hover:bg-purple-700" data-testid="storefront-signup-cta">
                  Get Started — It's Free
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-6 mt-8">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-center text-xs text-gray-400 mb-4">
            DealShaq — Surplus-Based Discount-Optimized Marketplace
          </p>
          <SiteDisclaimer />
        </div>
      </footer>
    </div>
  );
}
